/**
 * @fileoverview `buildDiscoverPickSet` — single source of truth for the DS
 * "Picked for you" pick-set model (WEB-677 / DPL-27).
 *
 * Replaces the single-event `buildDiscoverPickedForYouHero` (FRE-915) with a
 * ranked, up-to-three-card model. The FRE-915 gate is preserved EXACTLY (same
 * four conditions, same honest `tasteMatchPct` requirement on the anchor
 * pick) — this module only changes what happens once the gate holds: instead
 * of stopping at the single `pickedForYou` pointer, it backfills up to two
 * more cards from the SAME page of events, using their own honest
 * `tasteMatchPct`.
 *
 * **Honesty contract (epic guardrail).** Returns `null` (→ render nothing)
 * unless ALL hold:
 *   1. the viewer is authenticated,
 *   2. the authenticated envelope is present (anon responses omit it),
 *   3. `meta.personalized === true` (a real taste signal, not a soaking
 *      cold-start account),
 *   4. `envelope.pickedForYou` is present, AND
 *   5. the referenced event resolves in the page's `events` array and carries
 *      an honest `tasteMatchPct`.
 *
 * When the gate holds, the anchor pick (the `pickedForYou` pointer) always
 * leads. Up to two more cards backfill from other page events that ALSO
 * carry an honest `tasteMatchPct`, ranked by that percentage, descending.
 * "Honest absence" applies per-card too: the set renders exactly as many
 * cards as qualify (1, 2, or 3) — it is never padded to fill the grid.
 *
 * **Header copy.** `matchedCount` is the corpus-wide `meta.tasteMatchedTotal`
 * when the envelope supplies a number (WEB-786). The "Picked for you" /
 * "N of M taste matches" framing is the one that ships on that path.
 * `formatPickSetHeader` also implements the ticket's documented fallback
 * ("Best of what's still ahead" / "N of Total tonight", keyed off
 * `meta.total`) for a `matchedCount === 0` input — used when the server
 * field is absent (API-1671 degrade) so the caption never invents a
 * page-local `M`.
 *
 * @since WEB-677 (Discovery v2 — DPL-27), superseding FRE-915
 */
import type { CityCode } from '@loci/city-editions';
import type { EventImageUrls } from '@/lib/image-proxy/eventImageSources';
import {
  formatEventTimes,
  type DiscoverAuthenticatedEnvelope,
  type DiscoverEvent,
  type DiscoverPickedForYou,
  type EventCategory,
} from '@loci/types';

import { primaryCategoryLabel } from '@/config/categories';
import { categoryDisplayLabel } from '@/lib/city-home/categoryLabelCase';
import { listedWhyReasons } from '@/lib/discover/listedWhyReasons';
import { buildEventDetailHref, type EventDetailHrefSurface } from '@/lib/recommendations/eventHref';

/** At most this many pick cards render, regardless of how many qualify. */
const MAX_PICKS = 3;

/**
 * Gate inputs the builder reads. `isAuthenticated` comes from `useAuth()`;
 * `envelope`, `personalized`, and `total` come from the shared
 * `useDiscoverEventsQuery` subscription (no second fetch — the cold-load
 * single-fetch invariant is preserved).
 */
export interface DiscoverPickSetInput {
  /** `useAuth().isAuthenticated`. */
  readonly isAuthenticated: boolean;
  /** Authenticated-only envelope from the discover response. Absent for anon. */
  readonly envelope: DiscoverAuthenticatedEnvelope | undefined;
  /**
   * `meta.personalized` from the discover response. The gate requires an
   * explicit `true`; `false`/`undefined` keep the set hidden.
   */
  readonly personalized: boolean | undefined;
  /** `meta.total` — the unfiltered catalog total, used by the fallback header. */
  readonly total: number | undefined;
  /**
   * API-1671 corpus-wide `meta.tasteMatchedTotal`. When a number, the
   * header's "N of M taste matches" uses this `M` so it agrees with the
   * strip / hero / results bar (WEB-786). Null/omitted falls through to
   * the `matchedCount === 0` header, never a page-local tally.
   */
  readonly tasteMatchedTotal?: number | null;
  /** Resolved city code — drives each pick's detail href. */
  readonly cityCode: CityCode;
  /**
   * Host surface that owns the current Discover page (WEB-772). City /
   * fallback stay same-origin; app crosses to the marketing-city host.
   * Defaults to `fallback` so omitted callers keep staging slugs on-host.
   */
  readonly surface?: EventDetailHrefSurface;
}

/** A page event narrowed to carry an honest, present `tasteMatchPct`. */
type QualifyingEvent = DiscoverEvent & { tasteMatchPct: number };

/** Presentational view-model for a single pick card. */
export interface DiscoverPickCardModel {
  readonly eventId: string;
  readonly href: string;
  readonly title: string;
  /** Category slug driving the media slot's left-edge accent color. */
  readonly categorySlug: EventCategory | undefined;
  /**
   * A single honest reason line, or `null` when neither the curated
   * `pickedForYou.reason` nor a `whyReasons` entry backs one — the card then
   * omits the line entirely rather than inventing one.
   */
  readonly whyLine: string | null;
  /** Raw event image URL, or `null` when absent/blank (renders "Poster pending"). */
  readonly imageUrl: string | null;
  /**
   * WEB-817 — proxied PIP-235 size variants, forwarded from `DiscoverEvent`.
   * Lets the 56px poster slot pick the `thumbnail` band instead of handing
   * the optimizer the full-size cover. Absent for the many rows with no
   * variants, so `imageUrl` stays the fallback.
   */
  readonly imageUrls?: EventImageUrls;
  /**
   * Compact meta row segments — category signature, time, venue, price — with
   * any absent token already filtered out (honest-absence; never a dangling
   * separator).
   */
  readonly metaSegments: readonly string[];
}

/** Resolved pick-set model. `null` from the builder means "render nothing". */
export interface DiscoverPickSetModel {
  readonly eyebrow: string;
  readonly caption: string;
  readonly picks: readonly DiscoverPickCardModel[];
}

/**
 * Resolve the anchor pick out of the page's events by id. Exported for tests.
 * Returns `undefined` when no event on the page matches the pointer (e.g. the
 * highest-scoring event scrolled past the current page window) so the set
 * stays hidden rather than render against a stale / absent row.
 */
export function resolvePickedEvent(
  events: readonly DiscoverEvent[] | undefined,
  pickedForYou: DiscoverPickedForYou,
): DiscoverEvent | undefined {
  if (!events || events.length === 0) return undefined;
  return events.find((event) => event.id === pickedForYou.eventId);
}

/**
 * Compact category signature for the meta row (e.g. `MUSIC`,
 * `HOBBIES & INTERESTS`). Uses only the primary category — the pick card's
 * meta row has one line to spend on four tokens (signature, time, venue,
 * price). Returns `'EVENT'` when the event carries no category so the row
 * never renders a blank first segment.
 */
export function formatPickCategoryLabel(event: DiscoverEvent): string {
  return categoryDisplayLabel(primaryCategoryLabel(event.categories) ?? 'Event');
}

/** `7:30 PM` / `Time TBA` — honors the server-authoritative `startTimeKnown` signal. */
function formatPickTime(event: DiscoverEvent): string {
  const isTimeKnown = event.startTimeKnown ?? true;
  if (!isTimeKnown) return 'Time TBA';

  const times = formatEventTimes(event.startsAt, event.endsAt, event.timezone);
  const local = times.startTime.localTime; // "HH:mm" 24h venue-local
  const [hhRaw, mmRaw] = local.split(':');
  const hh24 = Number.parseInt(hhRaw ?? '0', 10) || 0;
  const period = hh24 >= 12 ? 'PM' : 'AM';
  const hour12 = hh24 % 12 === 0 ? 12 : hh24 % 12;
  return `${hour12}:${mmRaw ?? '00'} ${period}`;
}

/**
 * `$32` / `$20–$45` / `FREE`, or `null` when no honest price exists.
 * `priceMin` is a number ONLY when `priceKnown` is true (never coalesce a
 * `null` price into a fabricated `$0`).
 */
function formatPickPrice(event: DiscoverEvent): string | null {
  if (event.isFree) return 'Free';
  if (!event.priceKnown || event.priceMin === null) return null;
  const min = Math.round(event.priceMin);
  if (event.priceMax !== null && event.priceMax !== event.priceMin) {
    return `$${min}-$${Math.round(event.priceMax)}`;
  }
  return `$${min}`;
}

/** Compact signature + time + venue + price, honest-absence per token. */
function buildCardMetaSegments(event: DiscoverEvent): string[] {
  const segments: string[] = [formatPickCategoryLabel(event), formatPickTime(event)];

  const venue = event.venueName.trim();
  if (venue.length > 0) segments.push(venue);

  const price = formatPickPrice(event);
  if (price) segments.push(price);

  return segments;
}

/**
 * Single honest reason line. The anchor pick prefers the curated
 * `pickedForYou.reason` (a human-authored line); every other card, and the
 * anchor when no curated reason exists, falls back to the first server
 * `whyReasons` entry. Returns `null` when neither exists — the card then
 * omits the line rather than inventing one.
 */
function buildWhyLine(event: DiscoverEvent, curatedReason: string | undefined): string | null {
  const curated = curatedReason?.trim();
  if (curated && curated.length > 0) return curated;

  // WEB-1228 (R2): never `PROXIMITY` — it measures from the query center.
  const first = listedWhyReasons(event.whyReasons)[0]?.displayText.trim();
  return first && first.length > 0 ? first : null;
}

function buildCardModel(
  event: QualifyingEvent,
  cityCode: CityCode,
  curatedReason: string | undefined,
  surface: EventDetailHrefSurface,
): DiscoverPickCardModel {
  return {
    eventId: event.id,
    href: buildEventDetailHref(cityCode, event.slug, surface),
    title: event.title,
    categorySlug: event.categories[0],
    whyLine: buildWhyLine(event, curatedReason),
    imageUrl: event.imageUrl.trim().length > 0 ? event.imageUrl : null,
    // WEB-817 — forwarded verbatim; the card decides which band its slot wants.
    ...(event.imageUrls ? { imageUrls: event.imageUrls } : {}),
    metaSegments: buildCardMetaSegments(event),
  };
}

/**
 * Build the header eyebrow + caption. `matchedCount` is the corpus-wide
 * `meta.tasteMatchedTotal` (or `0` when that field is absent);
 * `total` is `meta.total`, the unfiltered catalog total.
 */
export interface DiscoverPickSetHeader {
  readonly eyebrow: string;
  readonly caption: string;
}

export function formatPickSetHeader(
  shown: number,
  matchedCount: number,
  total: number | undefined,
): DiscoverPickSetHeader {
  if (matchedCount > 0) {
    const noun = matchedCount === 1 ? 'taste match' : 'taste matches';
    return {
      eyebrow: 'Picked for you',
      caption: `${shown} of ${matchedCount} ${noun}`,
    };
  }
  if (typeof total === 'number' && total > 0) {
    return { eyebrow: "Best of what's still ahead", caption: `${shown} of ${total} tonight` };
  }
  const noun = shown === 1 ? 'pick' : 'picks';
  return { eyebrow: "Best of what's still ahead", caption: `${shown} ${noun}` };
}

/**
 * Build the gated pick-set model from the auth + discover-response inputs and
 * the page's events. Returns `null` (→ render nothing) unless the strict gate
 * holds and the anchor event resolves with an honest `tasteMatchPct`.
 *
 * The `events` argument is the page's `DiscoverEvent[]`; the anchor is looked
 * up there by `envelope.pickedForYou.eventId`, and the backfill cards are
 * drawn from the same array.
 */
export function buildDiscoverPickSet(
  input: DiscoverPickSetInput,
  events: readonly DiscoverEvent[] | undefined,
): DiscoverPickSetModel | null {
  const { isAuthenticated, envelope, personalized, total, tasteMatchedTotal, cityCode } = input;
  const surface = input.surface ?? 'fallback';

  // Strict gate — identical posture to the retired `buildDiscoverPickedForYouHero`.
  if (!isAuthenticated) return null;
  if (!envelope) return null;
  if (personalized !== true) return null;

  const pointer = envelope.pickedForYou;
  if (!pointer) return null;

  const anchorEvent = resolvePickedEvent(events, pointer);
  if (!anchorEvent) return null;

  // FRE-1418 — `tasteMatchPct` gates and orders the set but is never shown
  // (taste is reasons, not scores). When the anchor carries no
  // `tasteMatchPct` (signal too faint to clear the threshold), hide the set.
  if (typeof anchorEvent.tasteMatchPct !== 'number') return null;
  const anchor: QualifyingEvent = { ...anchorEvent, tasteMatchPct: anchorEvent.tasteMatchPct };

  const qualifying = (events ?? []).filter(
    (event): event is QualifyingEvent => typeof event.tasteMatchPct === 'number',
  );
  // WEB-786 — only a measured corpus-wide total may appear as `M` in
  // "N of M taste matches". Null/omitted is honest absence (API-1671
  // degrade) — do not substitute the page-local qualifying length.
  const matchedCount = typeof tasteMatchedTotal === 'number' ? tasteMatchedTotal : 0;

  const others = qualifying
    .filter((event) => event.id !== anchor.id)
    .sort((a, b) => b.tasteMatchPct - a.tasteMatchPct);

  const selected = [anchor, ...others].slice(0, MAX_PICKS);

  const picks = selected.map((event) =>
    buildCardModel(event, cityCode, event.id === anchor.id ? pointer.reason : undefined, surface),
  );

  const header = formatPickSetHeader(picks.length, matchedCount, total);

  return { eyebrow: header.eyebrow, caption: header.caption, picks };
}
